/**
 * MDM (Master Data Management) query key factory
 *
 * Shared across features -- ObjectTypes and ObjectRecords are the unified entity system.
 * All keys include tenantId for multi-tenant cache isolation.
 */

export const masterDataKeys = {
  all: ['mdm'] as const,

  // -- Object Types -----------------------------------------------------------
  objectTypeLists: () => [...masterDataKeys.all, 'object-types'] as const,
  objectTypes: (tenantId: string, filters?: Record<string, unknown>) =>
    [...masterDataKeys.objectTypeLists(), tenantId, filters] as const,
  objectType: (id: string, tenantId: string) =>
    [...masterDataKeys.all, 'object-type', id, tenantId] as const,
  objectTypeNextSuffix: (id: string, tenantId: string) =>
    [
      ...masterDataKeys.all,
      'object-type',
      id,
      'next-suffix',
      tenantId,
    ] as const,

  // -- Object Records ---------------------------------------------------------
  objectRecordLists: (typeId: string) =>
    [...masterDataKeys.all, 'object-records', typeId] as const,
  objectRecords: (
    typeId: string,
    tenantId: string,
    filters?: Record<string, unknown>
  ) =>
    [...masterDataKeys.objectRecordLists(typeId), tenantId, filters] as const,
  objectRecord: (typeId: string, recordId: string, tenantId: string) =>
    [
      ...masterDataKeys.all,
      'object-record',
      typeId,
      recordId,
      tenantId,
    ] as const,
  /** Prefix for every single-record detail query of a given type — use to
   *  invalidate them all at once (e.g. on a type-wide "records changed"
   *  signal that doesn't carry a specific record id). */
  objectRecordDetailsForType: (typeId: string) =>
    [...masterDataKeys.all, 'object-record', typeId] as const,
  /** Prefix for every batch slice query — use to invalidate them all at once. */
  objectRecordsBatchAll: () =>
    [...masterDataKeys.all, 'object-records-batch'] as const,
  /** Batch slice of records across several types in one request. */
  objectRecordsBatch: (
    typeIds: string[],
    tenantId: string,
    perTypeLimit: number,
    includeArchived = false
  ) =>
    [
      ...masterDataKeys.objectRecordsBatchAll(),
      tenantId,
      perTypeLimit,
      [...typeIds].sort(),
      includeArchived,
    ] as const,

  // -- Dependents ---------------------------------------------------------------
  recordDependents: (typeId: string, recordId: string, tenantId: string) =>
    [
      ...masterDataKeys.all,
      'dependents',
      'record',
      typeId,
      recordId,
      tenantId,
    ] as const,
};
